import { useEffect } from 'react';
import { Bot, Zap, MessageSquare } from 'lucide-react';
import type { Assistant } from '../../types/assistant';
import Badge from '../ui/Badge';
import ChatContainer from './ChatContainer';
import { useChat } from '../../hooks/useChat';

interface ChatInterfaceProps {
  assistant: Assistant;
}

// Assistant type icons
const ASSISTANT_TYPE_ICONS = {
  generic: Bot,
  mahia: Zap,
  orion: MessageSquare,
  pingy: MessageSquare,
  synergi: MessageSquare,
} as const;

function ChatInterface({ assistant }: ChatInterfaceProps) {
  const { messages, isProcessing, sendMessage, clearMessages, abortRequest } = useChat({
    apiEndpoint: '/assistants/conversation',
    requestPayload: { assistant_id: assistant.id },
    timeout: 300000, // 5 minutes
    onError: error => {
      console.error('Chat error:', error);
    },
  });

  // Reset messages when assistant changes
  useEffect(() => {
    clearMessages();
  }, [assistant.id, clearMessages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRequest();
    };
  }, [abortRequest]);

  const getAssistantIcon = (type: string) => {
    return ASSISTANT_TYPE_ICONS[type as keyof typeof ASSISTANT_TYPE_ICONS] || Bot;
  };

  const Icon = getAssistantIcon(assistant.type);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-[600px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {assistant.name}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <Badge variant="secondary" size="sm">
                {assistant.signature}
              </Badge>
              {assistant.current_status && (
                <div className="flex items-center space-x-1">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      assistant.current_status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="text-xs text-gray-500">
                    {assistant.current_status === 'active' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {assistant.summary && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{assistant.summary}</p>
        )}
      </div>

      {/* Chat Container */}
      <div className="flex-1 min-h-0">
        <ChatContainer
          messages={messages}
          onSendMessage={sendMessage}
          isProcessing={isProcessing}
          emptyStateMessage={`Inicie uma conversa com ${assistant.name}`}
        />
      </div>
    </div>
  );
}

export default ChatInterface;
